import { NavLink } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Tooltip } from "@/components/ui/Tooltip";
import { NAV_ITEMS, SECTION_LABEL_KEYS, type NavDef } from "@/config/navigation";
import { NAV_ICONS } from "@/layouts/navIcons";
import { YouTubeNavIcon } from "@/layouts/YouTubeNavIcon";
import { useUiStore } from "@/store/uiStore";
import { cn } from "@/utils/cn";

function groupBySection(items: NavDef[]) {
  const order: NavDef["section"][] = ["main", "create", "optimize", "system"];
  return order.map((section) => ({
    section,
    labelKey: SECTION_LABEL_KEYS[section],
    items: items.filter((i) => i.section === section),
  }));
}

export type SidebarProps = {
  className?: string;
  onNavigate?: () => void;
  forceExpanded?: boolean;
};

export function Sidebar({ className, onNavigate, forceExpanded = false }: SidebarProps) {
  const storeCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const t = useUiStore((s) => s.t);
  const locale = useUiStore((s) => s.locale);

  // If forceExpanded (e.g. inside mobile off-canvas drawer), override collapsed state
  const collapsed = forceExpanded ? false : storeCollapsed;
  const groups = groupBySection(NAV_ITEMS);

  return (
    <aside
      className={cn(
        "relative flex h-full flex-col border-r border-slate-200/80 bg-white transition-[width] duration-200 ease-in-out dark:border-zinc-800/80 dark:bg-zinc-950",
        collapsed ? "w-[72px]" : "w-[260px]",
        className,
      )}
      aria-label="Sidebar navigation"
    >
      {/* Brand area (rendered on desktop, or inside drawer if not provided by drawer header) */}
      {!forceExpanded ? (
        <div
          className={cn(
            "flex h-14 shrink-0 items-center border-b border-slate-100 px-4 dark:border-zinc-800/80",
            collapsed ? "justify-center px-2" : "justify-between",
          )}
        >
          <div className={cn("flex items-center min-w-0", collapsed ? "justify-center" : "gap-3")}>
            <BrandLogo
              className={cn(
                "shrink-0 rounded-lg transition-transform",
                collapsed ? "h-8 w-8 object-cover object-left" : "h-8 w-auto",
              )}
            />
            {!collapsed ? (
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold tracking-tight text-slate-900 dark:text-zinc-50">
                  {t("app.name")}
                </div>
                <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-[var(--brand-primary)]">
                  {t("app.shortName")}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Navigation list */}
      <nav
        className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4 [scrollbar-width:thin] [scrollbar-color:rgba(156,163,175,0.4)_transparent] dark:[scrollbar-color:rgba(113,113,122,0.4)_transparent]"
        key={locale}
        aria-label="Main Navigation"
      >
        {groups.map((group) => (
          <div key={group.section} className="space-y-1">
            {!collapsed ? (
              <div className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                {t(group.labelKey)}
              </div>
            ) : (
              <div className="my-2 border-t border-slate-100 dark:border-zinc-800/60" />
            )}
            <ul className="space-y-0.5" role="list">
              {group.items.map((item) => {
                const Icon = NAV_ICONS[item.icon];
                const label = t(item.labelKey);

                const linkElement = (
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    title={collapsed ? label : undefined}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-all duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]",
                        collapsed && "justify-center px-2",
                        isActive
                          ? "bg-[var(--brand-primary)] text-white shadow-xs font-semibold dark:bg-[var(--brand-primary)] dark:text-white [&_svg]:!text-white [&_svg]:stroke-white"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100",
                      )
                    }
                  >
                    {item.brandIcon === "youtube" ? (
                      <YouTubeNavIcon />
                    ) : (
                      <Icon className="h-4 w-4 shrink-0 transition-transform group-hover:scale-105" strokeWidth={1.9} />
                    )}
                    {!collapsed ? <span className="truncate">{label}</span> : null}
                  </NavLink>
                );

                return (
                  <li key={item.path}>
                    {collapsed ? (
                      <Tooltip content={label} placement="right" delayMs={100}>
                        {linkElement}
                      </Tooltip>
                    ) : (
                      linkElement
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Sidebar footer: Privacy link */}
      <div className="shrink-0 border-t border-slate-100 p-2 space-y-1 dark:border-zinc-800/80">
        {collapsed ? (
          <Tooltip content="Privacy Policy & Trust" placement="right" delayMs={100}>
            <NavLink
              to="/privacy"
              target="_blank"
              onClick={onNavigate}
              aria-label="Privacy Policy & Trust"
              className="flex items-center justify-center rounded-lg p-2 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100"
            >
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            </NavLink>
          </Tooltip>
        ) : (
          <NavLink
            to="/privacy"
            target="_blank"
            onClick={onNavigate}
            title="Privacy Policy & Trust"
            className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100"
          >
            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span className="truncate">Privacy Policy</span>
          </NavLink>
        )}
      </div>

      {/* Floating border arrow collapse toggle button */}
      {!forceExpanded ? (
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-40 hidden md:flex h-6 w-6 items-center justify-center rounded-full bg-[var(--brand-primary,#002FA7)] hover:bg-[var(--brand-hover,#001B66)] active:scale-95 text-white shadow-md border-2 border-white dark:border-zinc-950 transition-all duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)] select-none"
          data-testid="sidebar-collapse-button"
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5 stroke-[2.5]" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5 stroke-[2.5]" />
          )}
        </button>
      ) : null}
    </aside>
  );
}
